import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  alpha,
  LinearProgress,
  Button,
  Divider,
} from "@mui/material";
import {
  LineChart,
  Line,
  CartesianGrid,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";

import { Psychology as BrainIcon } from "@mui/icons-material";
import {
  lessonUrlMapping,
  getLessonTypeParam,
  formatDate,
  createUrlSlug,
  lessonMapping,
  availableSpeakers,
} from "./functions";

// Import Firebase
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";

// Import MUI icons
import SchoolIcon from "@mui/icons-material/School";
import CircleIcon from "@mui/icons-material/Circle";
import QuizIcon from "@mui/icons-material/Quiz";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatIcon from "@mui/icons-material/Chat";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ScienceIcon from "@mui/icons-material/Science";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ImageIcon from "@mui/icons-material/Image";
import MicIcon from "@mui/icons-material/Mic";
import { Grading as GradingIcon } from "@mui/icons-material";

// Styling
const COLORS = {
  flashcards: "#22C55E",
  quizzes: "#9333EA",
  podcasts: "#5760c4",
  lessons: "#3B82F6",
  essays: "#b45309",
  chats: "#EF4444",
  assistentChats: "#EF4444",
  hpTests: "#0f172a",
};

// Icon mapping for content types
const ICONS = {
  lessons: <SchoolIcon fontSize="small" />,
  flashcards: <ViewAgendaIcon fontSize="small" />,
  quizzes: <BrainIcon fontSize="small" />,
  podcasts: <MicIcon fontSize="small" />,

  essays: <GradingIcon fontSize="small" />,
  chats: <ChatIcon fontSize="small" />,
  assistentChats: <SmartToyIcon fontSize="small" />,
  images: <ImageIcon fontSize="small" />,
  hpTests: <ScienceIcon fontSize="small" />,
};

// Small progress card for distribution visualization
const ProgressCard = ({ label, value, color, onClick }) => {
  // Make background much lighter by reducing alpha to 0.05 or even lower
  const veryLightColor = alpha(color, 0.03);

  return (
    <Box
      sx={{
        display: "inline-block",
        width: "calc(50% - 8px)",
        mb: 1.5,
        mr: 1,
        textAlign: "center",
        p: 1.5,
        borderRadius: 2.5,
        backgroundColor: veryLightColor,
        border: "1px solid",
        borderColor: alpha(color, 0.08),
        cursor: "pointer",
        transition: "all 0.25s",
        "&:hover": {
          transform: "scale(1.03) translateY(-2px)",
        },
      }}
      onClick={onClick}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          display: "block",
          mt: 0.5,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const ShortcutButton = ({ icon, label, onClick, color }) => {
  const theme = useTheme();

  // Get color directly from props instead of icon props
  // If no color provided, use a default from COLORS
  const buttonColor = color || COLORS.lessons;

  return (
    <Box
      variant="outlined"
      startIcon={icon}
      onClick={onClick}
      fullWidth
      sx={{
        display: "flex",
        p: 1.2,
        borderRadius: "12px",
        alignItems: "center",
        border: `1px solid ${buttonColor}20`,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        mb: 1.5,
        textAlign: "left !important",
        textTransform: "none",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:focus-visible": {
          outline: `2px solid ${buttonColor}`,
          outlineOffset: "2px",
        },
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
          bgcolor: `${buttonColor}10`,
        },
      }}
    >
      <Box
        sx={{
          borderRadius: "10px",
          bgcolor: `${buttonColor}15`,
          p: 0.7,
          mr: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <CircleIcon sx={{ fontSize: "0.6rem", color: `${buttonColor}` }} />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            fontSize: "0.9rem",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

// Main dashboard component
const MyContentDash = ({ user, styles, role }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State for content data
  const [flashcards, setFlashcards] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [essays, setEssays] = useState([]);
  const [chats, setChats] = useState([]);
  const [assistentChats, setAssistentChats] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [hpTests, setHpTests] = useState([]);
  const [images, setImages] = useState([]);

  // Loading state
  const [loading, setLoading] = useState({
    flashcards: true,
    quizzes: true,
    podcasts: true,
    essays: true,
    lessons: true,
    chats: true,
    assistentChats: true,
    hpTests: true,
    all: true,
    images: true,
  });

  const fetchUserImages = async () => {
    setLoading((prev) => ({ ...prev, images: true, all: true }));
    try {
      const imagesRef = collection(db, "images");
      const imagesQuery = query(
        imagesRef,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(32),
      );

      const querySnapshot = await getDocs(imagesQuery);
      const imageItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.imageName || "Genererad bild",
          date: formatDate(data.createdAt || Date.now()),
          category: data.category || "Image",
          createdAt: data.createdAt || 0,
          type: "images",
        };
      });

      setImages(imageItems);
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoading((prev) => ({ ...prev, images: false }));
    }
  };

  // Fetch data functions
  const fetchUserFlashcards = async () => {
    setLoading((prev) => ({ ...prev, flashcards: true, all: true }));
    try {
      const flashcardsQuery = query(
        collection(db, "flashcardCollections"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(28),
      );

      const querySnapshot = await getDocs(flashcardsQuery);
      const flashcardItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.collectionName || "Untitled Collection",
          date: formatDate(data.createdAt || Date.now()),
          cards: data.flashcards?.length || 0,
          category: data.category || "General",
          slug: createUrlSlug(data.collectionName || "untitled"),
          flashcards: data.flashcards || [],
          type: "flashcards",
          createdAt: data.createdAt,
        };
      });

      setFlashcards(flashcardItems);
    } catch (error) {
      console.error("Error fetching flashcards:", error);
    } finally {
      setLoading((prev) => ({ ...prev, flashcards: false }));
    }
  };

  const fetchUserQuizzes = async () => {
    setLoading((prev) => ({ ...prev, quizzes: true, all: true }));
    try {
      const quizzesQuery = query(
        collection(db, "quiz"),
        where("userId", "==", user.uid),
      );

      const querySnapshot = await getDocs(quizzesQuery);

      const quizItems = querySnapshot.docs
        .map((doc) => {
          const data = doc.data();

          // Handle different date formats
          let createdAtDate = data.createdAt;
          if (typeof data.createdAt === "string") {
            createdAtDate = new Date(data.createdAt);
          }

          return {
            id: doc.id,
            title: data.quizName || "Untitled Quiz",
            date: formatDate(createdAtDate),
            questions: data.results?.length || 0,
            category: data.category || "General",
            results: data.results || [],
            slug: createUrlSlug(data.quizName || "untitled"),
            createdAt: createdAtDate,
            type: "quizzes",
          };
        })
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, 6);

      setQuizzes(quizItems);
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    } finally {
      setLoading((prev) => ({ ...prev, quizzes: false }));
    }
  };

  const fetchUserPodcasts = async () => {
    setLoading((prev) => ({ ...prev, podcasts: true, all: true }));
    try {
      const podcastsQuery = query(
        collection(db, "podcasts"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(16),
      );

      const querySnapshot = await getDocs(podcastsQuery);
      const podcastItems = querySnapshot.docs
        .filter((doc) => {
          const data = doc.data();
          return !data.hasOwnProperty("shared") || data.shared !== true;
        })
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.podcastName || "Untitled Podcast",
            date: formatDate(data.createdAt || Date.now()),
            speaker1: data.speaker1,
            speaker2: data.speaker2,
            category: "podcast",
            slug: createUrlSlug(data.podcastName || "untitled"),
            createdAt: data.createdAt || 0,
            type: "podcasts",
          };
        });

      setPodcasts(podcastItems);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
    } finally {
      setLoading((prev) => ({ ...prev, podcasts: false }));
    }
  };

  const fetchUserEssays = async () => {
    setLoading((prev) => ({ ...prev, essays: true, all: true }));
    try {
      const essaysQuery = query(
        collection(db, "essays"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(23),
      );

      const querySnapshot = await getDocs(essaysQuery);
      const essayItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.fileName || "Untitled Essay",
          date: formatDate(data.createdAt || Date.now()),
          category: data.rubricName || "Essay",
          slug: createUrlSlug(data.fileName || "untitled"),
          createdAt: data.createdAt || 0,
          type: "assessments",
          grade: data.scores?.overallScore?.grade || null,
        };
      });

      setEssays(essayItems);
    } catch (error) {
      console.error("Error fetching essays:", error);
    } finally {
      setLoading((prev) => ({ ...prev, essays: false }));
    }
  };

  const fetchUserLessons = async () => {
    setLoading((prev) => ({ ...prev, lessons: true, all: true }));
    try {
      const lessonsRef = collection(db, "lesson_usage");
      const regularQuery = query(
        lessonsRef,
        where("userId", "==", user.uid),
        where("hasPrinted", "==", true),
        orderBy("timestamp", "desc"),
        limit(57),
      );

      const querySnapshot = await getDocs(regularQuery);
      const lessonItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.lessonName || "Untitled Lesson",
          date: formatDate(data.timestamp || Date.now()),
          category: lessonMapping(data.lessonType) || "Lesson",
          slug: createUrlSlug(data.lessonName || "untitled"),
          createdAt: data.timestamp || 0,
          type: "lessons",
          lessonType: data.lessonType,
          tags: data.tags || [],
        };
      });

      setLessons(lessonItems);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    } finally {
      setLoading((prev) => ({ ...prev, lessons: false }));
    }
  };

  const fetchUserChats = async () => {
    setLoading((prev) => ({ ...prev, chats: true, all: true }));
    try {
      const chatsRef = collection(db, "chats");
      const chatsQuery = query(
        chatsRef,
        where("userId", "==", user.uid),
        where("conversationType", "==", "chat"),
        orderBy("lastMessageSent", "desc"), // Sort by lastMessageSent instead of createdAt
        limit(15),
      );

      const querySnapshot = await getDocs(chatsQuery);
      const chatItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          title: data.name || "Untitled Chat",
          date: formatDate(data.lastMessageSent || data.createdAt), // Use lastMessageSent if available
          category: "AI Chat",
          slug: createUrlSlug(data.name || "untitled"),
          firstMessageContent: data.firstMessageContent,
          lastMessageContent: data.lastMessageContent, // Can display preview if needed
          createdAt: data.lastMessageSent || data.createdAt, // For sorting
          type: "chats",
        };
      });

      setChats(chatItems);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, chats: false }));
    }
  };

  const fetchUserAssistentChats = async () => {
    setLoading((prev) => ({ ...prev, assistentChats: true, all: true }));
    try {
      // First, get all the user's assistants/teachers
      const teachersCollection = collection(db, "teachers");
      const teachersQuery = query(
        teachersCollection,
        where("userId", "==", user.uid),
      );

      const teachersSnapshot = await getDocs(teachersQuery);
      const teachersList = teachersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Use a single query to get the latest message for each assistant
      // This is more efficient than querying for each assistant individually
      const conversationsRef = collection(db, "conversations");
      const latestMessagesQuery = query(
        conversationsRef,
        where("userId", "==", user.uid),
        where(
          "conversationType",
          "in",
          teachersList.map((t) => t.id),
        ),
        orderBy("createdAt", "desc"),
      );

      const messagesSnapshot = await getDocs(latestMessagesQuery);

      // Group messages by conversationType (teacher/assistant ID)
      const latestMessagesByTeacher = {};
      messagesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const teacherId = data.conversationType;

        if (
          !latestMessagesByTeacher[teacherId] ||
          data.createdAt > latestMessagesByTeacher[teacherId].createdAt
        ) {
          latestMessagesByTeacher[teacherId] = data;
        }
      });

      // Create the final list with the latest message information
      const assistentChatsList = teachersList.map((teacher) => {
        const latestMessage = latestMessagesByTeacher[teacher.id];

        return {
          id: teacher.id,
          title: teacher.characterName || "Unnamed Assistant",
          date: latestMessage
            ? formatDate(latestMessage.createdAt)
            : formatDate(teacher.createdAt || Date.now()),
          category: teacher.category || "Assistant",
          slug: createUrlSlug(teacher.characterName || "unnamed-assistant"),
          imageUrl: teacher.imageUrl,
          type: "assistentChats",
          lastMessageContent: latestMessage?.message,
          lastMessage: latestMessage?.message,
          createdAt: latestMessage?.createdAt ||
            teacher.createdAt || { seconds: 0 },
        };
      });

      // Sort by most recent message
      assistentChatsList.sort((a, b) => {
        const aTimestamp = a.createdAt.seconds || 0;
        const bTimestamp = b.createdAt.seconds || 0;
        return bTimestamp - aTimestamp;
      });

      setAssistentChats(assistentChatsList);
    } catch (error) {
      console.error("Error fetching assistant chats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, assistentChats: false }));
    }
  };

  const fetchUserHpTests = async () => {
    setLoading((prev) => ({ ...prev, hpTests: true, all: true }));
    try {
      const q = query(
        collection(db, "hpTestProgress"),
        where("userId", "==", user.uid),
        limit(9),
      );
      const querySnapshot = await getDocs(q);
      const hpTestItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          testId: data.testId,
          title: data.testName || "Okänt test",
          date: formatDate(data.lastUpdated || Date.now()),
          testInProgress: data.testInProgress || false,
          answeredQuestionsCount: data.results ? data.results.length : 0,
          totalQuestions: data.totalQuestions || 0,
          timeLeft: data.timeLeft || 0,
          results: data.results || [],
          type: "hpTests",
          createdAt: data.lastUpdated || 0,
        };
      });
      setHpTests(hpTestItems);
    } catch (error) {
      console.error("Error fetching HP tests:", error);
    } finally {
      setLoading((prev) => ({ ...prev, hpTests: false }));
    }
  };

  // Effect to fetch data when user changes or reloadTrigger is activated
  useEffect(() => {
    if (user?.uid) {
      fetchUserFlashcards();
      fetchUserQuizzes();
      fetchUserPodcasts();
      fetchUserEssays();
      fetchUserLessons();
      fetchUserChats();
      fetchUserAssistentChats();
      fetchUserImages();
      if (role === "student") {
        fetchUserHpTests();
      }
    }
  }, [user?.uid, role]);

  // Update all loading state when all individual states are done
  useEffect(() => {
    if (
      !loading.flashcards &&
      !loading.quizzes &&
      !loading.podcasts &&
      !loading.essays &&
      !loading.lessons &&
      !loading.chats &&
      !loading.images &&
      !loading.assistentChats &&
      (role !== "student" || !loading.hpTests)
    ) {
      setLoading((prev) => ({ ...prev, all: false }));
    }
  }, [
    loading.flashcards,
    loading.quizzes,
    loading.podcasts,
    loading.essays,
    loading.lessons,
    loading.chats,
    loading.assistentChats,
    loading.hpTests,
    loading.images,
    role,
  ]);

  // Content counts
  const totalFlashcards = flashcards.length;
  const totalQuizzes = quizzes.length;
  const totalPodcasts = podcasts.length;
  const totalLessons = lessons.length;
  const totalEssays = essays.length;
  const totalChats = chats.length;
  const totalAssistentChats = assistentChats.length;
  const totalHpTests = hpTests.length;
  const totalImages = images.length;

  const totalContent =
    totalFlashcards +
    totalQuizzes +
    totalPodcasts +
    totalLessons +
    totalEssays +
    totalChats +
    totalAssistentChats +
    totalImages +
    (role === "student" ? totalHpTests : 0);

  // Content type config
  const contentTypes = [
    {
      id: "essays",
      name: "Bedömningar",
      count: totalEssays,
      color: COLORS.essays,
      icon: ICONS.essays,
    },
    {
      id: "quizzes",
      name: "Quiz",
      count: totalQuizzes,
      color: COLORS.quizzes,
      icon: ICONS.quizzes,
    },
    {
      id: "lessons",
      name: "Lektioner",
      count: totalLessons,
      color: COLORS.lessons,
      icon: ICONS.lessons,
    },

    {
      id: "flashcards",
      name: "Flashcards",
      count: totalFlashcards,
      color: COLORS.flashcards,
      icon: ICONS.flashcards,
    },

    {
      id: "podcasts",
      name: "Podcasts",
      count: totalPodcasts,
      color: COLORS.podcasts,
      icon: ICONS.podcasts,
    },

    {
      id: "images",
      name: "Bilder",
      count: totalImages,

      color: "#10B981",
      icon: ICONS.images,
    },
    {
      id: "chats",
      name: "Chattar",
      count: totalChats,
      color: COLORS.chats,
      icon: ICONS.chats,
    },
    {
      id: "assistentChats",
      name: "Assistenter",
      count: totalAssistentChats,
      color: COLORS.assistentChats,
      icon: ICONS.assistentChats,
    },
    // Add this object to the contentTypes array
  ];

  if (role === "student") {
    contentTypes.push({
      id: "hpTests",
      name: "HP Tester",
      count: totalHpTests,
      color: COLORS.hpTests,
      icon: ICONS.hpTests,
    });
  }

  // Filter to non-empty content types
  const activeTypes = contentTypes.filter((type) => type.count > 0);

  // Recent activity items
  const allContent = [
    ...flashcards.map((item) => ({ ...item, type: "flashcards" })),
    ...quizzes.map((item) => ({ ...item, type: "quizzes" })),
    ...podcasts.map((item) => ({ ...item, type: "podcasts" })),
    ...lessons.map((item) => ({ ...item, type: "lessons" })),
    ...essays.map((item) => ({ ...item, type: "essays" })),
    ...chats.map((item) => ({ ...item, type: "chats" })),
    ...images.map((item) => ({ ...item, type: "images" })),

    ...assistentChats.map((item) => ({ ...item, type: "assistentChats" })),
    ...(role === "student"
      ? hpTests.map((item) => ({ ...item, type: "hpTests" }))
      : []),
  ];

  // Sort by recency
  allContent.sort((a, b) => {
    const getTimestamp = (item) => {
      if (!item.createdAt) return 0;
      if (item.createdAt.seconds) return item.createdAt.seconds;
      if (item.createdAt instanceof Date)
        return item.createdAt.getTime() / 1000;
      if (typeof item.createdAt === "number") return item.createdAt / 1000;
      return 0;
    };
    return getTimestamp(b) - getTimestamp(a);
  });

  // Get 5 most recent items
  const recentItems = allContent.slice(0, 5);

  // Generate activity data by weekday
  const generateWeekdayActivityData = () => {
    const days = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
    const weekdayCounts = Array(7).fill(0);

    allContent.forEach((item) => {
      const timestamp = getTimestamp(item.createdAt);
      if (timestamp) {
        const date = new Date(timestamp * 1000);
        const day = date.getDay() === 0 ? 6 : date.getDay() - 1; // Adjust to start from Monday
        weekdayCounts[day]++;
      }
    });

    return days.map((day, i) => ({
      name: day,
      count: weekdayCounts[i],
    }));
  };

  // Helper function to get timestamp
  const getTimestamp = (createdAt) => {
    if (!createdAt) return 0;
    if (createdAt.seconds) return createdAt.seconds;
    if (createdAt instanceof Date) return createdAt.getTime() / 1000;
    if (typeof createdAt === "number") return createdAt / 1000;
    return 0;
  };

  // Generate weekday activity data
  const activityData = generateWeekdayActivityData();

  // Handle item click - using same function from MyContent.js
  const handleItemClick = (item) => {
    switch (item.type) {
      case "flashcards":
        navigate(`/app/flashcards/${item.id}/${item.slug || "untitled"}`);
        break;
      case "quizzes":
        navigate(`/app/quiz/${item.id}/${item.slug || "untitled"}`);
        break;
      case "podcasts":
        navigate(`/app/podcast/${item.id}`);
        break;
      case "hpTests":
        navigate(`/app/hp/${item.testId}`);
        break;
      case "essays":
      case "assessments":
        navigate(`/app/uppsats/${item.id}`);
        break;
      case "assistentChats":
        navigate(`/app/chat/${item.id}`);
        break;

      case "images":
        navigate(`/app/mina-bilder`);
        break;

      case "lessons":
        navigate(
          `/app${item.lessonType !== "PowerPointGenerator.js" ? "/lektioner" : ""}/${lessonUrlMapping(item.lessonType)}/${item.id.substring(0, 10)}${getLessonTypeParam(item.lessonType) === "default" ? "" : `?type=${getLessonTypeParam(item.lessonType)}`}`,
        );
        break;
      case "chats":
        navigate(`/app/fraga-ai/${item.id}`);
        break;
      default:
        navigate(`/app/${item.type}/${item.id}`);
    }
  };

  const shortcuts = [
    {
      label: "Ny bedömning",
      link: "/app/uppsats/ny",
      icon: <AssignmentIcon />,
      color: COLORS.essays,
    },

    {
      label: "Nytt quiz",
      link: "/app/quiz",
      icon: <PlaylistAddIcon />,
      color: COLORS.quizzes,
    },

    {
      label: "Ny lektion",
      link: "/app/lektioner",
      icon: <AddIcon />,
      color: COLORS.lessons,
    },

    {
      label: "Nya flashcards",
      link: "/app/flashcards",
      icon: <NoteAddIcon />,
      color: COLORS.flashcards,
    },
    {
      label: "Ny podcast",
      link: "/app/podcast",
      icon: <HeadphonesIcon />,
      color: COLORS.podcasts,
    },

    {
      label: "Aktivera för elever",
      link: "/app/rum",
      icon: <AssignmentIcon />,
      color: "#000000",
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: { xs: "flex", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box
          component="h2"
          sx={{
            fontSize: 22,
            width: "100%",
            textAlign: "left",
            fontWeight: "600",
            color: "text.primary",
            m: 0,
          }}
        >
          Min aktivitet
        </Box>
      </Box>
      <Paper
        elevation={0}
        sx={{
          ...(styles?.freshBox || {}),
          ...(styles?.softBoxShadow || {}),
          p: { xs: 2, sm: 4 },
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden",
          backgroundImage: "none",
          mt: 1.5,
        }}
      >
        {loading.all ? (
          <>
            {/* Loading skeletons for the three column layout */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Column 3: Shortcuts loading */}
              {/* Column 3: Shortcuts - Always show directly */}
              <Grid item xs={12} md={3}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Genvägar
                  </Typography>
                </Box>
                {shortcuts.map((shortcut, index) => {
                  const isLastItem = shortcut.label === "Aktivera för elever";
                  if (role === "student" && shortcut.label === "Ny lektion") {
                    return null;
                  }
                  return (
                    <React.Fragment key={index}>
                      {isLastItem && (
                        <Divider
                          sx={{
                            my: 2.5,
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "transparent"
                                : "transparent",
                          }}
                        />
                      )}
                      <ShortcutButton
                        label={shortcut.label}
                        icon={shortcut.icon}
                        color={shortcut.color}
                        onClick={() => navigate(shortcut.link)}
                      />
                    </React.Fragment>
                  );
                })}
              </Grid>
              {/* Column 2: Content Counts loading */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Mitt innehåll
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                  {[...Array(8)].map((_, idx) => (
                    <Skeleton
                      key={idx}
                      variant="rectangular"
                      sx={{
                        width: "calc(50% - 8px)",
                        height: 72,
                        borderRadius: 2.5,
                        mb: 1.5,
                        mr: idx % 2 === 0 ? 1 : 0,
                      }}
                      animation="wave"
                    />
                  ))}
                </Box>
              </Grid>
              {/* Column 1: Recent Activity loading */}
              <Grid item xs={12} md={5}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Senaste aktivitet
                  </Typography>
                </Box>
                {[...Array(5)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="rectangular"
                    sx={{
                      height: 64,
                      borderRadius: 2,
                      mb: 1,
                    }}
                    animation="wave"
                  />
                ))}
              </Grid>
            </Grid>

            {/* Graph loading skeleton */}
            <Grid container>
              <Grid item xs={12}>
                <Box
                  sx={{ mb: 2, display: "flex", alignItems: "center", mt: 2 }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Aktivitet
                  </Typography>
                </Box>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: "100%",
                    height: 250,
                    borderRadius: 2,
                  }}
                  animation="wave"
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            {/* Top row - 3 equally sized columns */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Column 3: Genvägar (replacing Fördelning) */}
              <Grid item xs={12} md={3}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Genvägar
                  </Typography>
                </Box>
                {shortcuts.map((shortcut, index) => {
                  // Check if this is the last item (Aktivera för elever)
                  const isLastItem = shortcut.label === "Aktivera för elever";

                  // Don't render the "Ny lektion" for students
                  if (role === "student" && shortcut.label === "Ny lektion") {
                    return null;
                  }

                  return (
                    <React.Fragment key={index}>
                      {/* Add a divider before the last item */}
                      {isLastItem && (
                        <Divider
                          sx={{
                            my: 2.5,
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "transparent"
                                : "transparent",
                          }}
                        />
                      )}
                      <ShortcutButton
                        label={shortcut.label}
                        icon={shortcut.icon}
                        color={shortcut.color}
                        onClick={() => navigate(shortcut.link)}
                      />
                    </React.Fragment>
                  );
                })}
              </Grid>

              {/* Column 2: Content Counts (more compact) */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Mitt innehåll
                  </Typography>
                </Box>

                {contentTypes.slice(0, 8).map((type, i) => (
                  <ProgressCard
                    key={i}
                    label={type.name}
                    value={type.count}
                    max={totalContent}
                    color={type.color}
                    type={type.id}
                    onClick={() => {
                      // Navigation logic based on content type
                      switch (type.id) {
                        case "flashcards":
                          navigate("/app/mina-flashcards");
                          break;
                        case "quizzes":
                          navigate("/app/mina-quiz");
                          break;
                        case "podcasts":
                          navigate("/app/mina-podcasts");
                          break;
                        case "lessons":
                          navigate("/app/delade-lektioner");
                          break;
                        case "essays":
                          navigate("/app/uppsatser");
                          break;
                        case "images":
                          navigate(`/app/mina-bilder`);
                          break;
                        case "chats":
                          navigate("/app/fraga-ai");
                          break;
                        case "assistentChats":
                          navigate("/app/larare");
                          break;
                        case "hpTests":
                          navigate("/app/hp");
                          break;
                        default:
                          navigate(`/app/${type.id}`);
                      }
                    }}
                  />
                ))}
              </Grid>
              {/* Column 1: Recent Activity */}
              <Grid item xs={12} md={5}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "text.secondary",
                    }}
                  >
                    Senaste aktivitet
                  </Typography>
                </Box>
                {recentItems.length > 0 ? (
                  recentItems.map((item, idx) => {
                    const typeConfig = contentTypes.find(
                      (t) => t.id === item.type,
                    );
                    return (
                      <Box
                        key={idx}
                        onClick={() => handleItemClick(item)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? alpha(typeConfig?.color || "#999", 0.2)
                              : alpha(typeConfig?.color || "#999", 0.08)
                          }`,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha(typeConfig?.color || "#999", 0.05)
                              : alpha(typeConfig?.color || "#999", 0.03),
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(typeConfig?.color || "#999", 0.1)
                                : alpha(typeConfig?.color || "#999", 0.06),
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            mr: 2,
                            color: typeConfig?.color || "#999",
                            display: "flex",
                            alignItems: "center", // This centers the icon vertically
                            justifyContent: "center", // This centers the icon horizontally in its box
                            height: "100%", // Make sure box takes full height of parent
                          }}
                        >
                          {typeConfig?.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {(item.title || "Untitled").substring(0, 30) +
                              (item.title?.length > 30 ? "..." : "")}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.7rem",
                            }}
                          >
                            {item.date || ""}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: typeConfig?.color || "text.secondary",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            fontSize: "0.65rem",
                            letterSpacing: "0.5px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(typeConfig?.color || "#999", 0.15)
                                : alpha(typeConfig?.color || "#999", 0.1),
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                          }}
                        >
                          {typeConfig?.name || ""}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                      fontSize: "0.85rem",
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.02)
                          : alpha("#000", 0.01),
                    }}
                  >
                    Inget innehåll genererat ännu
                  </Box>
                )}
              </Grid>
            </Grid>
          </>
        )}
        {/* Bottom row - full width graph */}
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", mt: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "text.secondary",
                }}
              >
                Aktivitet
              </Typography>
            </Box>

            <Box sx={{ width: "100%", height: 250 }}>
              {activityData.some((d) => d.count > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={activityData}
                    margin={{ top: 15, right: 30, left: 20, bottom: 15 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorActivity"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.primary.main}
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.primary.main}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.1)
                          : alpha("#000", 0.07)
                      }
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fontWeight: 500 }}
                      stroke={
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.6)
                          : alpha("#000", 0.6)
                      }
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fontWeight: 500 }}
                      stroke={
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.6)
                          : alpha("#000", 0.6)
                      }
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#fff",
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        fontSize: "0.85rem",
                        padding: "10px 14px",
                      }}
                      formatter={(value) => [
                        `${value} genereringar`,
                        "Aktivitet",
                      ]}
                      labelFormatter={(value) => `${value}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorActivity)"
                      dot={{
                        fill: theme.palette.primary.main,
                        strokeWidth: 2,
                        stroke:
                          theme.palette.mode === "dark" ? "#1e293b" : "#fff",
                        r: 6,
                      }}
                      activeDot={{
                        fill: theme.palette.primary.main,
                        strokeWidth: 2,
                        stroke:
                          theme.palette.mode === "dark" ? "#1e293b" : "#fff",
                        r: 8,
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    fontSize: "0.85rem",
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha("#fff", 0.01)
                        : alpha("#000", 0.01),
                  }}
                >
                  Ingen aktivitet registrerad
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default MyContentDash;
